import { useState } from 'react';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import IncidentEditMemberDialog from 'components/IncidentConfig/IncidentEditMemberDialog';
import IncidentNonActiveConfirmDialog from 'components/IncidentConfig/IncidentNonActiveConfirmDialog';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import RosterTable from 'components/IncidentConfig/roster/RosterTable';
import { RosterViewMode } from 'components/IncidentConfig/roster/types';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

type DeleteType = 'Leave Incident' | 'Remove Member' | 'Remove Scheduled';

type DeleteTypeConfig = {
  title: string;
  content: string;
  buttonText: string;
};

const DELETE_TYPE_CONFIG: Record<DeleteType, DeleteTypeConfig> = {
  'Leave Incident': {
    title: 'Leave Incident Confirmation',
    content: 'Do you want to remove yourself from this incident?',
    buttonText: 'Leave Incident',
  },
  'Remove Member': {
    title: 'Remove Member Confirmation',
    content: 'Do you want to delete this incident Member',
    buttonText: 'Remove Member',
  },
  'Remove Scheduled': {
    title: 'Delete Schedule Confirmation',
    content: 'Are you sure you want to delete this scheduled?',
    buttonText: 'Delete Schedule',
  },
};

type IncidentRosterViewProps = {
  rosterData: RosterEntry[];
  isRosterLoading: boolean;
  currentUserHasApprovalAuthority?: boolean;
  isTogglingAuthority?: boolean;
  onRemoveMember: (member: RosterEntry) => void;
  onRemoveSelf: (member: RosterEntry) => void;
  onMarkActive: (member: RosterEntry) => void;
  onDeleteScheduleConfirmed: (params: {
    scheduleId: string;
    userGuid: string;
    incidentMemberId: string;
  }) => void;
  onToggleApprovalAuthority?: (member: RosterEntry) => void;
  showApprovalAuthority?: boolean;
  isDraft?: boolean;
};

const IncidentRosterView = ({
  rosterData,
  isRosterLoading,
  currentUserHasApprovalAuthority,
  isTogglingAuthority,
  onRemoveMember,
  onRemoveSelf,
  onMarkActive,
  onDeleteScheduleConfirmed,
  onToggleApprovalAuthority,
  showApprovalAuthority = true,
  isDraft = false,
}: IncidentRosterViewProps) => {
  const [memberSelectedForEditing, setMemberSelectedForEditing] =
    useState<RosterEntry>();
  const [isEditMemberDialogOpen, setIsEditMemberDialogOpen] = useState(false);
  const [notActiveMemberDialogOpen, setNotActiveMemberDialogOpen] =
    useState(false);
  const [deactivateRow, setDeactivateRow] = useState<RosterEntry>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteType, setDeleteType] = useState<DeleteType | null>(null);
  const [deleteMemberData, setDeleteMemberData] = useState<RosterEntry | null>(
    null
  );
  const [deleteScheduleId, setDeleteScheduleId] = useState<string>();
  const [viewMode, setViewMode] = useState<RosterViewMode>('orgchart');

  const editMemberButtonClicked = (member: RosterEntry) => {
    setMemberSelectedForEditing(member);
    setIsEditMemberDialogOpen(true);
  };

  const removeMemberButtonClicked = (member: RosterEntry) => {
    setDeleteType('Remove Member');
    setDeleteMemberData(member);
    setShowConfirmDialog(true);
  };

  const removeSelfFromIncidentButtonClicked = (member: RosterEntry) => {
    setDeleteType('Leave Incident');
    setDeleteMemberData(member);
    setShowConfirmDialog(true);
  };

  const onDeleteSchedule = (scheduleId: string, row: RosterEntry) => {
    setDeleteType('Remove Scheduled');
    setDeleteScheduleId(scheduleId);
    setDeleteMemberData(row);
    setShowConfirmDialog(true);
  };

  const onDeleteConfirmation = () => {
    if (!deleteMemberData) {
      return;
    }

    if (deleteType === 'Leave Incident') {
      onRemoveSelf(deleteMemberData);
    } else if (deleteType === 'Remove Member') {
      onRemoveMember(deleteMemberData);
    } else if (deleteType === 'Remove Scheduled' && deleteScheduleId) {
      onDeleteScheduleConfirmed({
        scheduleId: deleteScheduleId,
        userGuid: deleteMemberData.userId,
        incidentMemberId: deleteMemberData.incidentMemberId ?? '',
      });
    }

    setShowConfirmDialog(false);
    setDeleteMemberData(null);
    setDeleteScheduleId(undefined);
    setDeleteType(null);
  };

  const handleToggleButtonClick = (value: string, row: RosterEntry) => {
    if (value === 'Checked-In') {
      if (row.active && row.activeTime) {
        return;
      }
      onMarkActive(row);
      return;
    }

    if (value === 'Checked-Out') {
      if (!(row.active && row.activeTime)) {
        return;
      }
      setDeactivateRow(row);
      setNotActiveMemberDialogOpen(true);
    }
  };

  const deleteConfig = deleteType ? DELETE_TYPE_CONFIG[deleteType] : null;

  return (
    <>
      <RosterActions
        isDraft={isDraft}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === 'table' ? (
        <div className="form-block">
          <RosterTable
            rosterData={rosterData}
            isRosterLoading={isRosterLoading}
            editMemberButtonClicked={editMemberButtonClicked}
            removeMemberButtonClicked={removeMemberButtonClicked}
            removeSelfFromIncidentButtonClicked={
              removeSelfFromIncidentButtonClicked
            }
            onDeleteSchedule={onDeleteSchedule}
            onToggleButtonClick={handleToggleButtonClick}
            simplified={false}
            currentUserHasApprovalAuthority={currentUserHasApprovalAuthority}
            onToggleApprovalAuthority={onToggleApprovalAuthority}
            isTogglingAuthority={isTogglingAuthority}
            showApprovalAuthority={showApprovalAuthority}
          />
        </div>
      ) : (
        !!rosterData.length && (
          <div className="my-4">
            <OrganizationChart incidentMembers={rosterData} isDraft={isDraft} />
          </div>
        )
      )}

      {isEditMemberDialogOpen && memberSelectedForEditing && (
        <IncidentEditMemberDialog
          show={isEditMemberDialogOpen}
          onClose={() => setIsEditMemberDialogOpen(false)}
          member={memberSelectedForEditing}
        />
      )}

      {notActiveMemberDialogOpen && (
        <IncidentNonActiveConfirmDialog
          show={notActiveMemberDialogOpen}
          onClose={() => setNotActiveMemberDialogOpen(false)}
          active_time={deactivateRow?.activeTime}
          user_guid={deactivateRow?.userId}
          incident_member_id={deactivateRow?.incidentMemberId}
        />
      )}

      {showConfirmDialog && deleteConfig && (
        <StylishConfirmDialog
          show={showConfirmDialog}
          dialogTitle={deleteConfig.title}
          dialogContent={deleteConfig.content}
          dialogButtonText={deleteConfig.buttonText}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={onDeleteConfirmation}
        />
      )}
    </>
  );
};

export default IncidentRosterView;
